import { createContext, useContext, useEffect, useState } from 'react';
import { SERVER_GATE, isConfigured, isUnlocked, lock, unlock } from './auth';

const PreviewAuthContext = createContext({ lock: () => {} });
export const usePreviewAuth = () => useContext(PreviewAuthContext);

function PasswordForm({ onUnlock }) {
  const [password, setPassword] = useState('');
  const [failures, setFailures] = useState(0);
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    document.title = 'Preview | Sign in';
  }, []);

  function submit(event) {
    event.preventDefault();
    if (waiting) return;
    if (unlock(password)) {
      onUnlock();
      return;
    }
    // a short, growing pause after each wrong attempt
    setFailures((n) => n + 1);
    setPassword('');
    setWaiting(true);
    setTimeout(() => setWaiting(false), Math.min(500 * (failures + 1), 4000));
  }

  if (!isConfigured()) {
    return (
      <div className="blog-container">
        <div className="status-message">
          <h2>Preview is switched off</h2>
          <p>No master password has been set. Set <code>VITE_PREVIEW_PASSWORD</code> in <code>frontend/.env</code> and restart the site.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-container">
      <form className="gate" onSubmit={submit}>
        <h1>Preview</h1>
        <p>Enter the master password to preview and edit articles that are not yet public.</p>
        <label htmlFor="preview-password">Master password</label>
        <input
          id="preview-password"
          type="password"
          autoComplete="current-password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {failures > 0 && <p className="field-error" role="alert">That password is not correct.</p>}
        <button type="submit" className="btn btn-primary" disabled={waiting || !password}>
          Open preview
        </button>
      </form>
    </div>
  );
}

export default function PreviewGuard({ children }) {
  const [unlocked, setUnlocked] = useState(isUnlocked);
  if (SERVER_GATE) {
    // The server already asked for a login; "Lock" just leaves the editor.
    return (
      <PreviewAuthContext.Provider value={{ lock: () => { window.location.href = '/'; } }}>
        {children}
      </PreviewAuthContext.Provider>
    );
  }
  if (!unlocked) return <PasswordForm onUnlock={() => setUnlocked(true)} />;
  const value = {
    lock: () => {
      lock();
      setUnlocked(false);
    },
  };
  return <PreviewAuthContext.Provider value={value}>{children}</PreviewAuthContext.Provider>;
}
